/**
 * lib/whatsapp.js — Messages WhatsApp GRATUITS via liens wa.me
 * ============================================================
 * Aucune API WhatsApp payante : on ouvre simplement l'app WhatsApp de l'utilisateur
 * avec le destinataire et le message déjà pré-remplis (il ne reste qu'à appuyer sur
 * « Envoyer »). Tous les messages existent en français, darija et anglais.
 *
 *   https://wa.me/<numero>?text=<message encodé>          -> vers un contact précis
 *   https://wa.me/?text=<message encodé>                  -> laisse choisir (ex : groupe)
 */

const site = () => (typeof window !== "undefined" ? window.location.origin : "");
const digits = (phone) => (phone || "").replace(/[^0-9]/g, "");

// Messages d'INVITATION (nouveau compte) — trilingues.
const INVITE = {
  fr: (u) =>
    `Bonjour ${u.name},\n\nVotre accès à l'application de la copropriété *Rahma Bât A* est prêt.\n\n1) Ouvrez ce lien : ${site()}/activate\n2) Votre identifiant : *${u.identifier}*\n3) Votre code d'invitation : *${u.invite_code}*\n\nVous choisirez ensuite votre code secret (PIN). Le code d'invitation expire dans 30 jours.`,
  ar: (u) =>
    `السلام ${u.name},\n\nحساب ديالك فتطبيق الإقامة *رحمة عمارة A* واجد.\n\n1) حل هاد الرابط : ${site()}/activate\n2) المعرف ديالك : *${u.identifier}*\n3) كود الدعوة : *${u.invite_code}*\n\nمن بعد غادي تختار كود سري (PIN). كود الدعوة كيسالح صلاحيتو من بعد 30 يوم.`,
  en: (u) =>
    `Hello ${u.name},\n\nYour access to the *Rahma Bldg A* condo app is ready.\n\n1) Open this link: ${site()}/activate\n2) Your username: *${u.identifier}*\n3) Your invitation code: *${u.invite_code}*\n\nYou will then choose your secret PIN. The invitation code expires in 30 days.`,
};

// Messages de RÉINITIALISATION du PIN — trilingues.
const RESET = {
  fr: (u) =>
    `Bonjour ${u.name},\n\nVotre code secret a été réinitialisé.\n\n1) Ouvrez : ${site()}/activate\n2) Identifiant : *${u.identifier}*\n3) Nouveau code d'invitation : *${u.invite_code}*\n\nChoisissez un nouveau code secret (PIN).`,
  ar: (u) =>
    `السلام ${u.name},\n\nالكود السري ديالك تعاود تعيّن من جديد.\n\n1) حل : ${site()}/activate\n2) المعرف : *${u.identifier}*\n3) كود دعوة جديد : *${u.invite_code}*\n\nختار كود سري جديد (PIN).`,
  en: (u) =>
    `Hello ${u.name},\n\nYour secret code has been reset.\n\n1) Open: ${site()}/activate\n2) Username: *${u.identifier}*\n3) New invitation code: *${u.invite_code}*\n\nChoose a new secret PIN.`,
};

function build(number, message) {
  const base = number ? `https://wa.me/${number}` : "https://wa.me/";
  return `${base}?text=${encodeURIComponent(message)}`;
}

export function waInviteLink(u) {
  const lang = u.lang || "fr";
  return build(digits(u.phone), (INVITE[lang] || INVITE.fr)(u));
}

export function waResetLink(u) {
  const lang = u.lang || "fr";
  return build(digits(u.phone), (RESET[lang] || RESET.fr)(u));
}

// Lien générique (ex : partager un message dans le groupe WhatsApp de l'immeuble).
export function waShare(message, number = "") {
  return build(digits(number), message);
}

// Libellés trilingues pour les messages partagés dans le groupe de l'immeuble.
const L = {
  fr: { app: "Rahma Bât A", report: "Résumé financier", revenue: "Recettes", expense: "Dépenses",
        saved: "Épargne", bank: "Solde banque", cash: "Solde caisse", currency: "MAD",
        pdf: "📄 Le rapport PDF détaillé est téléchargeable dans l'application.",
        announcement: "📢 Annonce", alert: "⚠️ Alerte", open: "👉 Ouvrir l'application :" },
  ar: { app: "رحمة عمارة A", report: "ملخص مالي", revenue: "المداخيل", expense: "المصاريف",
        saved: "التوفير", bank: "رصيد البنك", cash: "رصيد الصندوق", currency: "درهم",
        pdf: "📄 تقرير PDF المفصل يمكن تحميلو من التطبيق.",
        announcement: "📢 إعلان", alert: "⚠️ تنبيه", open: "👉 حل التطبيق :" },
  en: { app: "Rahma Bldg A", report: "Financial summary", revenue: "Income", expense: "Expenses",
        saved: "Savings", bank: "Bank balance", cash: "Cash balance", currency: "MAD",
        pdf: "📄 The detailed PDF report can be downloaded in the app.",
        announcement: "📢 Announcement", alert: "⚠️ Alert", open: "👉 Open the app:" },
};
const lbl = (lang) => L[lang] || L.fr;
const money = (n) => Number(n || 0).toLocaleString("fr-FR");

// Rapport financier à partager dans le groupe WhatsApp de l'immeuble.
export function waReport({ period, revenue, expense, saved, bank, cash, lang = "fr" }) {
  const s = lbl(lang);
  const msg = [
    `*${s.app}* — ${s.report} ${period}`, "",
    `${s.revenue} : ${money(revenue)} ${s.currency}`,
    `${s.expense} : ${money(expense)} ${s.currency}`,
    `${s.saved} : ${money(saved)} ${s.currency}`,
    `${s.bank} : ${money(bank)} ${s.currency}`,
    `${s.cash} : ${money(cash)} ${s.currency}`, "",
    s.pdf, `${s.open} ${site()}`,
  ].join("\n");
  return build("", msg);
}

// Annonce à partager dans le groupe.
export function waAnnouncement({ title, body, lang = "fr" }) {
  const s = lbl(lang);
  return build("", [`${s.announcement} — *${s.app}*`, "", `*${title}*`, body || "", "", `${s.open} ${site()}`].filter(Boolean).join("\n"));
}

// Alerte (ex : anomalie de rapprochement, dépense en espèces) à partager/transférer.
export function waAlert({ title, body, number = "", lang = "fr" }) {
  const s = lbl(lang);
  return build(digits(number), [`${s.alert} — *${s.app}*`, "", `*${title}*`, body || "", "", `${s.open} ${site()}`].filter(Boolean).join("\n"));
}

// Libellés trilingues pour les Assemblées Générales.
const AG = {
  fr: { conv: "📅 Convocation — Assemblée Générale", kind: { ordinaire: "ordinaire", extraordinaire: "extraordinaire" },
        when: "Date", place: "Lieu", agenda: "Ordre du jour", results: "📊 Les résultats de l'AG sont disponibles.", open: "👉 Ouvrir l'application :" },
  ar: { conv: "📅 استدعاء — الجمع العام", kind: { ordinaire: "عادي", extraordinaire: "استثنائي" },
        when: "التاريخ", place: "المكان", agenda: "جدول الأعمال", results: "📊 نتائج الجمع العام متوفرة.", open: "👉 حل التطبيق :" },
  en: { conv: "📅 Notice — General Assembly", kind: { ordinaire: "ordinary", extraordinaire: "extraordinary" },
        when: "Date", place: "Location", agenda: "Agenda", results: "📊 The assembly results are available.", open: "👉 Open the app:" },
};

// Convocation à l'AG à partager dans le groupe WhatsApp de l'immeuble.
export function waConvocation({ title, kind = "ordinaire", when = "", place = "", agenda = [], lang = "fr" }) {
  const s = AG[lang] || AG.fr;
  const a = lbl(lang);
  const lines = [`${s.conv} — *${a.app}*`, "", `*${title}* (${s.kind[kind] || kind})`];
  if (when) lines.push(`${s.when} : ${when}`);
  if (place) lines.push(`${s.place} : ${place}`);
  if (agenda.length) { lines.push("", `${s.agenda} :`); agenda.forEach((it, i) => lines.push(`${i + 1}. ${it}`)); }
  lines.push("", `${s.open} ${site()}`);
  return build("", lines.join("\n"));
}

// Annonce des résultats d'une AG clôturée.
export function waAGResults({ title, lang = "fr" }) {
  const s = AG[lang] || AG.fr;
  const a = lbl(lang);
  return build("", [`${s.results} — *${a.app}*`, "", `*${title}*`, "", `${s.open} ${site()}`].join("\n"));
}
